import * as React from 'react';
import {
  AuthorizationInfo,
  ScopedAuthorizationRights,
  FlightMembership,
} from '../../Common/Utilities/AuthorizationInfo';
import { UserContext } from '../../Models/UserProfile';
import { useState } from 'react';
import { store } from '../../Store';
import { DropdownDataState } from '../Reducers/DropdownData';
import { DropdownService } from 'src/Services/DropdownService';
import { useDispatch } from 'react-redux';
import { setDropdownData } from 'src/actions/Actions';
import { Spinner, SpinnerSize } from '@fluentui/react';
import { appInsights } from 'src/Services/Insights';
import { HttpVerbs, Settings, UATConstants } from 'src/Common/Models/UATConstants';
import { ServiceFactoryClientExecuteApi } from 'src/Common/Services/ServiceFactoryToExecuteApi';
import ErrorResponse from 'src/Models/ErrorResponse';
import { useLocation } from 'react-router-dom';
import { fetchWithIndexedDB } from 'src/Common/Services/IndexedDBService';
import { getTenantId, selectTenants } from 'src/Common/Models/StoreConstants';
import { ITenant } from 'src/tenantConfig/interface';
import { isTenantFlightUnauthorized } from 'src/utils/flightGatedTenants';
import AccessRestrictionPage from './AccessRestrictionPage';

export enum TargetRedirection {
  ErrorPage,
  HomePage,
}

interface AuthorizationProps {
  requiredPermissions?: ScopedAuthorizationRights[];
  user: UserContext;
  redirectTarget?: TargetRedirection;
  title?: string;
  message?: string;
  inPlaceMessage?: string;
  children?: any;
  checkAny?: boolean;
  isMainPage?: boolean;
  requiredFlights?: string[];
  flightMembership?: FlightMembership;
  callBack?: () => void;
  errorCallBack?: () => void;
  isDialogVisible: boolean;
  isField?: boolean;
  loadDropDown?: boolean;
  vacTenant?: ITenant[];
}

const Authorize: React.FC<AuthorizationProps> = ({ ...authorizationProps }) => {
  const [showAHPM, setShowAHPM] = useState(false);
  const [ahpmLoaded, setShowAhpmLoaded] = useState(false);
  const location = useLocation();

  // return true if user is in org or if user has required permissions
  const checkUserHasRequiredPermissions = (): boolean => {
    // Some tenants gate their Manage Action page behind a flight (see FLIGHT_GATED_TENANTS).
    // A user without the matching flight who opens such a tenant URL directly is denied here,
    // regardless of org membership. Returns false only for a gated tenant with a missing flight;
    // it is a no-op for every non-gated tenant/route. To gate a new tenant, add an entry to
    // FLIGHT_GATED_TENANTS — no code change here.
    const requestedTenantId =
      new URL(window.location.href).searchParams.get('tenantId') ?? undefined;
    if (isTenantFlightUnauthorized(requestedTenantId, authorizationProps.user.flights)) {
      return false;
    }

    if (
      authorizationProps.user.isUserInOrg === UATConstants.PlaceholdersAndTitles.YES &&
      authorizationProps.user.isUserExcluded === UATConstants.PlaceholdersAndTitles.NO
    ) {

      const tenantId = UATConstants.TenantIds.MCAPS_HELP;
      const params = new URL(window.location.href).searchParams;
      const urlTenantId = params.get('tenantId');
      if (urlTenantId === tenantId) {
        const tenantList = authorizationProps?.vacTenant;
        const tenantAuthorizations = authorizationProps.user.authorization.TenantAuthorizations;
        const mcapsHelpPMEnabled = !!tenantList?.find((tenant: ITenant) =>
          tenant.Default &&
          tenant.Enabled &&
          tenant.TenantID === tenantId
        );
        if (tenantAuthorizations?.some(auth => auth.TenantID === urlTenantId)) {
          return true;
        }
        if (urlTenantId === tenantId && !mcapsHelpPMEnabled) {
          return false;
        }
      }
      return true;
    }

    if (authorizationProps.checkAny) {
      // If any of the required permission exists, return true
      if (
        AuthorizationInfo.isIncludedInRequiredFlights(
          authorizationProps.user.flights,
          authorizationProps.requiredFlights,
          authorizationProps.flightMembership
        )
      ) {
        return true;
      }

      appInsights.trackEvent({
        name: 'User denied - Flights missing',
        properties: {
          username: authorizationProps.user.displayName,
          email: authorizationProps.user.email,
          flights: authorizationProps.user.flights,
          requiredFlights: authorizationProps.requiredFlights,
          userInOrg: authorizationProps.user.isUserInOrg,
          userExclude: authorizationProps.user.isUserExcluded,
        },
      });

      return false;
    }

    // If user is excluded exclusively,deny access
    if (authorizationProps.user.isUserExcluded === UATConstants.PlaceholdersAndTitles.YES) {
      appInsights.trackEvent({
        name: 'User denied - Excluded from access',
        properties: {
          username: authorizationProps.user.displayName,
          email: authorizationProps.user.email,
          flights: authorizationProps.user.flights,
        },
      });

      return false;
    }

    // return true as user is not denied access and has all flights needed
    return true;
  };

  const checkUserHasRequiredFlights = (): boolean => {
    // TODO: Need to load from Application Context reducer
    const userHasRequiredFlights = AuthorizationInfo.isIncludedInRequiredFlights(
      authorizationProps.user.flights,
      authorizationProps.requiredFlights,
      authorizationProps.flightMembership
    );
    return userHasRequiredFlights;
  };

  const [userHasRequiredPermissions, setUserHasRequiredPermissions] = useState<boolean>(
    checkUserHasRequiredPermissions()
  );
  const [userHasRequiredFlights, setUserHasRequiredFlights] = useState<boolean>(
    checkUserHasRequiredFlights()
  );
  const [reRender, setReRender] = useState<boolean>(false);
  const isLoadingDropdownDataRef = React.useRef<boolean>(false);
  const dispatch = useDispatch();
  React.useEffect(() => {
    if (authorizationProps.loadDropDown) {
      const dropDownData: DropdownDataState = store.getState().dropdownData;
      if (!dropDownData.isDataLoaded && !isLoadingDropdownDataRef.current) {
        getDropdownData();
      }
    }
  }, [userHasRequiredPermissions]);

  React.useEffect(() => {
    if (location.pathname === UATConstants.Routes.CreateActionPageForAHPM) {
      getGroupUsers();
    }
  }, []);

  const getGroupUsers = () => {
    ServiceFactoryClientExecuteApi.ExecuteRequest<any>(
      UATConstants.ApiPaths.GetGroupMembers,
      [],
      HttpVerbs.get,
      null,
      (response: any) => {
        if (response) {
          const user = response.find(
            (u: any) =>
              u.userPrincipalName === authorizationProps.user.email ||
              u.mail === authorizationProps.user.email
          );
          setShowAHPM(!!user);
          setShowAhpmLoaded(true);
        }
      },
      (response: ErrorResponse) => {
        console.error(response);
      },
      false
    );
  };

  const getDropdownData = async () => {
    if (isLoadingDropdownDataRef.current) {
      return;
    }
    try {
      isLoadingDropdownDataRef.current = true;
      const cachedDropdownData = await fetchWithIndexedDB(
        UATConstants.IndexedDBKeyNames.CosmosDropdownData, // Unique key for caching dropdown data
        async () => {
          // Fetch dropdown data from the service
          return new Promise<DropdownDataState>((resolve, reject) => {
            DropdownService.fetchDropdownData((response: DropdownDataState) => {
              response.isDataLoaded = true;
              resolve(response);
            }, reject);
          });
        },
        Settings.IDB_CACHE_DURATION
      );

      cachedDropdownData.isDataLoaded = true;

      // Dispatch the cached or fetched data to the Redux store
      dispatch(setDropdownData(cachedDropdownData));
      setReRender(true);
      isLoadingDropdownDataRef.current = false;
    } catch (error) {
      console.error('Error fetching dropdown data:', error);
      isLoadingDropdownDataRef.current = false;
    }
  };

  //TODO: Cache!
  if (authorizationProps.loadDropDown) {
    const dropDownValues: DropdownDataState = store.getState().dropdownData;
    if (location.pathname === UATConstants.Routes.CreateActionPageForAHPM) {
      if (userHasRequiredPermissions && (!dropDownValues.isDataLoaded || !ahpmLoaded)) {
        return (
          <Spinner
            size={SpinnerSize.large}
            label={UATConstants.PlaceholdersAndTitles.Loading}
            ariaLive='assertive'
            className='loading-panel-full'
          />
        );
      }
      if (!showAHPM) {
        return <AccessRestrictionPage />;
      }
    }
    if (userHasRequiredPermissions && dropDownValues.isDataLoaded) {
      return authorizationProps.children;
    }
    if (userHasRequiredPermissions && !dropDownValues.isDataLoaded) {
      return (
        <Spinner
          size={SpinnerSize.large}
          label={UATConstants.PlaceholdersAndTitles.Loading}
          ariaLive='assertive'
          className='loading-panel-full'
        />
      );
    }
  } else {
    if (userHasRequiredPermissions) {
      return authorizationProps.children;
    }
  }

  if (authorizationProps.inPlaceMessage !== undefined) {
    return <span>{authorizationProps.inPlaceMessage}</span>;
  }

  return authorizationProps.isMainPage ? <AccessRestrictionPage /> : null;
};

export default Authorize;
// Generated by GitHub Copilot | Updated on: 2026-10-12 | 48 lines modified | Reused shared access restriction page
// Generated by GitHub Copilot | Updated on: 2026-10-12 | 26 lines modified | Extracted restriction page component
